import { useCallback, useEffect, useRef, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import {
  createRoomDocument,
  ensureParticipant,
  updateParticipantHeartbeat,
  writeTodoSnapshot,
  getRoom,
  db,
} from "../firebase";

const useRoomTodos = ({ roomId, participantId, displayName }) => {
  const [localTodos, setLocalTodosState] = useState([]);
  const [peerTodos, setPeerTodos] = useState([]);
  const [status, setStatus] = useState("idle");
  const namesRef = useRef({});
  const pendingWrite = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!roomId || !participantId) return;
    let active = true;
    const init = async () => {
      setStatus("connecting");
      const snapshot = await getRoom(roomId);
      if (!snapshot.exists()) {
        await createRoomDocument({
          roomId,
          creatorName: displayName,
          creatorId: participantId,
        });
      }
      await ensureParticipant({ roomId, participantId, displayName });
      if (!active) return;
      setStatus("ready");
    };

    init().catch((error) => {
      console.error("Failed to init room todos", error);
      if (!active) return;
      setStatus("error");
    });

    return () => {
      active = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [displayName, participantId, roomId]);

  useEffect(() => {
    if (!roomId || !participantId) return undefined;
    const interval = setInterval(() => {
      updateParticipantHeartbeat({ roomId, participantId }).catch((error) =>
        console.error("Heartbeat error", error)
      );
    }, 20000);
    return () => clearInterval(interval);
  }, [participantId, roomId]);

  useEffect(() => {
    if (!roomId) return undefined;
    const participants = collection(db, "rooms", roomId, "participants");
    const unsubscribe = onSnapshot(participants, (snapshot) => {
      const names = {};
      snapshot.forEach((doc) => {
        names[doc.id] = doc.data()?.displayName || "Guest";
      });
      namesRef.current = names;
      setPeerTodos((current) =>
        current.map((peer) => ({
          ...peer,
          displayName: names[peer.id] || peer.displayName,
        }))
      );
    });
    return unsubscribe;
  }, [roomId]);

  useEffect(() => {
    if (!roomId || !participantId) return undefined;
    const todosCollection = collection(db, "rooms", roomId, "todos");
    const unsubscribe = onSnapshot(todosCollection, (snapshot) => {
      const peers = [];
      snapshot.forEach((doc) => {
        const data = doc.data() || {};
        const tasks = Array.isArray(data.todos) ? data.todos : [];
        if (doc.id === participantId) {
          setLocalTodosState(tasks);
        } else {
          peers.push({
            id: doc.id,
            displayName: namesRef.current[doc.id] || "Guest",
            todos: tasks,
          });
        }
      });
      setPeerTodos(peers);
    });

    return unsubscribe;
  }, [participantId, roomId]);

  const pushTodos = useCallback(
    (items) => {
      if (!roomId || !participantId) return;
      pendingWrite.current = items;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        writeTodoSnapshot({
          roomId,
          participantId,
          todos: pendingWrite.current || [],
        }).catch((error) => console.error("Failed to sync todos", error));
      }, 400);
    },
    [participantId, roomId]
  );

  const setLocalTodos = useCallback(
    (items) => {
      setLocalTodosState(items);
      pushTodos(items);
    },
    [pushTodos]
  );

  return { localTodos, setLocalTodos, peerTodos, status };
};

export default useRoomTodos;

